import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Sparkles, 
  Target, 
  Clock, 
  TrendingUp, 
  ShieldCheck, 
  ArrowUpRight,
  Info,
  type LucideIcon 
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tooltip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CountUp } from './CountUp';
import { LIST_CONTAINER, LIST_ITEM } from '../lib/motion';

interface SparkBarsProps {
  bars?: number[];
  tone?: 'emerald' | 'primary' | 'rose' | 'amber' | 'slate';
}

export function MiniSparkBars({
  bars = [35, 60, 85, 100, 70],
  tone = 'slate',
}: SparkBarsProps) {
  const barColors: Record<string, { active: string; inactive: string }> = {
    emerald: { active: 'bg-emerald-500', inactive: 'bg-emerald-200/70 dark:bg-emerald-950/60' },
    primary: { active: 'bg-blue-600 dark:bg-blue-500', inactive: 'bg-blue-200/70 dark:bg-blue-950/60' },
    rose: { active: 'bg-rose-500', inactive: 'bg-rose-200/70 dark:bg-rose-950/60' },
    amber: { active: 'bg-amber-500', inactive: 'bg-amber-200/70 dark:bg-amber-950/60' },
    slate: { active: 'bg-slate-700 dark:bg-slate-300', inactive: 'bg-slate-300/80 dark:bg-slate-800' },
  };

  const colors = barColors[tone] || barColors.slate;

  return (
    <span className="inline-flex items-end gap-[2px] h-3.5 pb-0.5 select-none" aria-hidden="true">
      {bars.map((heightPct, idx) => (
        <span
          key={idx}
          style={{ height: `${Math.max(20, heightPct)}%` }}
          className={cn(
            'w-[2.5px] rounded-full transition-all duration-200',
            heightPct >= 50 ? colors.active : colors.inactive
          )}
        />
      ))}
    </span>
  );
}

interface MetricTileProps {
  label: string;
  value: number;
  decimals?: number;
  suffix?: string;
  prefix?: string;
  icon: LucideIcon;
  bars?: number[];
  barTone?: 'emerald' | 'primary' | 'rose' | 'amber' | 'slate';
  valueColor?: string;
  description: string;
}

function MetricTile({
  label,
  value,
  decimals,
  suffix,
  prefix,
  icon: Icon,
  bars,
  barTone,
  valueColor,
  description,
}: MetricTileProps) {
  const isZero = value === 0;
  const activeBars = isZero ? [20, 20, 20, 20, 20] : (bars || [35, 60, 85, 100, 70]);
  const activeTone = isZero ? 'slate' : (barTone || 'primary');

  const tooltipContent = (
    <div className="w-64 space-y-1.5 p-1">
      <div className="flex items-center justify-between border-b border-slate-700/60 pb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {label}
        </span>
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-400 hover:text-emerald-300">
          VIEW DETAILS
          <ArrowUpRight className="h-3 w-3" />
        </span>
      </div>
      <p className="text-xs leading-relaxed text-slate-200">{description}</p>
    </div>
  );

  return (
    <Tooltip content={tooltipContent} placement="top" maxWidth="20rem" className="block w-full">
      <div className="group/tile relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:border-primary/50 hover:shadow-xs cursor-pointer w-full">
        {/* Left Icon Pill */}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-300 transition-all duration-200 group-hover/tile:bg-primary/10 dark:group-hover/tile:bg-primary/20 group-hover/tile:text-primary group-hover/tile:scale-105">
          <Icon className="h-4 w-4" />
        </div>

        {/* Right Metric Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span
              className={cn(
                'text-lg font-bold tracking-tight text-slate-900 dark:text-white tabular-nums leading-tight',
                valueColor
              )}
            >
              <CountUp value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
            </span>
            <MiniSparkBars bars={activeBars} tone={activeTone} />
          </div>
          <span className="inline-block text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 group-hover/tile:border-slate-600 dark:group-hover/tile:border-slate-400 transition-colors whitespace-nowrap">
            {label}
          </span>
        </div>
      </div>
    </Tooltip>
  );
}

export interface LeadsStats {
  total?: number;
  new?: number;
  conversionRate?: number;
  avgResponseTime?: number;
}

export function LeadsStatsBar({ stats }: { stats?: LeadsStats }) {
  return (
    <motion.div
      variants={LIST_CONTAINER}
      initial="hidden"
      animate="show"
      className="grid grid-cols-1 gap-3.5 lg:grid-cols-2"
    >
      {/* Pod 1: Customer & Lead Economics */}
      <motion.div variants={LIST_ITEM}>
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
          {/* Header Strip */}
          <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-3.5 py-2">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary/20 text-primary">
                <Users className="h-3 w-3" />
              </span>
              <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                Lead Pipeline
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-800/80 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                LIVE
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 font-medium">
              <span>All 4 channels connected</span>
            </div>
          </div>

          {/* Body: Metric Capsules */}
          <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 bg-white/70 dark:bg-slate-950/40">
            <MetricTile
              label="TOTAL LEADS"
              value={stats?.total ?? 0}
              icon={Users}
              bars={[40, 65, 85, 100, 75]}
              barTone="primary"
              description="Total prospective customer conversations captured across Facebook, Instagram, WhatsApp, and Web Chat."
            />
            <MetricTile
              label="NEW LEADS"
              value={stats?.new ?? 0}
              icon={Sparkles}
              bars={[30, 50, 70, 95, 60]}
              barTone="emerald"
              description="Uncontacted prospects that recently initiated an inquiry and are awaiting automated or human follow-up."
            />
          </div>
        </Card>
      </motion.div>

      {/* Pod 2: Performance & Conversion Telemetry */}
      <motion.div variants={LIST_ITEM}>
        <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
          {/* Header Strip */}
          <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-3.5 py-2">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                <ShieldCheck className="h-3 w-3" />
              </span>
              <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
                Conversion & Speed
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200/80 dark:border-indigo-800/80 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.2 text-[9px] font-bold text-indigo-700 dark:text-indigo-400">
                MEASURED
              </span>
            </div>
            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Real-time sync</span>
          </div>

          {/* Body: Metric Capsules */}
          <div className="grid grid-cols-2 gap-2.5 p-2.5 sm:p-3 bg-white/70 dark:bg-slate-950/40">
            <MetricTile
              label="CONVERSION RATE"
              value={stats?.conversionRate ?? 0}
              decimals={stats?.conversionRate && !Number.isInteger(stats.conversionRate) ? 1 : 0}
              suffix="%"
              icon={Target}
              bars={[45, 60, 80, 100, 85]}
              barTone="emerald"
              description="Percentage of lead engagements that successfully converted into completed Shopify orders or confirmed sales."
            />
            <MetricTile
              label="AVG RESPONSE"
              value={stats?.avgResponseTime ?? 0}
              suffix="h"
              icon={Clock}
              bars={[90, 70, 50, 30, 20]}
              barTone="amber"
              description="Average turnaround time taken by AI automations and support operators to reply to inbound customer queries."
            />
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}
